"use client";

import { formatWorkspaceDate } from "@/lib/display-preferences";

import { useCallback, useEffect, useState, useRef } from "react";
import { apiFetch } from "@/lib/client-api";
import {
  mediaArticleTemplate,
  mediaOutputFormats,
  mediaVariants,
  type MediaArticleDraft,
  type MediaDraft,
  type MediaGeneratedAsset,
  type MediaOutputType,
  type MediaPlatformDraft,
  type MediaState,
} from "@/lib/media";
import { mediaDraftText } from "@/lib/media-export";
import MediaInspiration,{ReferenceImage} from "./media-inspiration";
import {chooseMediaReferences,type MediaReference} from "@/lib/media-references";
import {useWorkspaceData} from "./workspace-context";
import MediaStyleEditor from "./media-style-editor";
import ScriptPreview from "./script-preview";
import {mediaProviderLabels,resolveMediaModelRouting,type MediaModelRouting} from "@/lib/media-model-routing";

type Busy = "load" | "generate" | null;

function MediaRouting({routing, mediaType}: {routing:MediaModelRouting; mediaType:MediaOutputType}) {
  return <div className="media-model-routing" aria-label="Models for this draft">
    <p className="muted">{routing.referenceAnalysis ? "Writing & references" : "Writing"} · {mediaProviderLabels[routing.writer.provider]}
      {routing.image && <> · {mediaType === "instagram-reel" ? "Cover image" : "Images"} · OpenAI ({routing.image.model})</>}
      {mediaType === "instagram-post" && !routing.image && <> · Typography · built in</>}
    </p>
    {routing.writerFallback && <p className="muted">{routing.writerFallback}</p>}
    {routing.imageFallback && <p className="muted">{routing.imageFallback}</p>}
  </div>;
}

function timeLabel(value: string) {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : formatWorkspaceDate(parsed, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatLabel(type: MediaOutputType) {
  return mediaOutputFormats.find((format) => format.id === type)?.label ?? type;
}

function draftMetric(draft: MediaDraft) {
  switch (draft.type) {
    case "article": return `${draft.wordCount} words`;
    case "x-post":
    case "threads-post": return `${draft.content.posts.length} ${draft.content.posts.length === 1 ? "post" : "posts"}`;
    case "instagram-post": return `${draft.content.slides.length} ${draft.content.slides.length === 1 ? "frame" : "frames"}`;
    case "youtube-video": return `${Math.round(draft.content.durationSeconds / 60)} min`;
    case "youtube-live":
    case "twitch-live": return `${draft.content.durationMinutes} min live`;
    case "instagram-reel":
    case "youtube-short":
    case "tiktok": return `${draft.content.durationSeconds} sec`;
  }
}

function openCitation(event: React.MouseEvent<HTMLAnchorElement>) {
  const target = document.getElementById(event.currentTarget.hash.slice(1));
  if (!target) return;
  let parent = target.closest("details");
  while (parent) { parent.open = true; parent = parent.parentElement?.closest("details") ?? null; }
  event.preventDefault();
  target.scrollIntoView({ block: "center", behavior: "smooth" });
  target.setAttribute("tabindex", "-1");
  target.focus({ preventScroll: true });
}

function CitedText({ text, draft }: { text: string; draft: MediaArticleDraft }) {
  return text.split(/(\[\d+\])/g).map((part, index) => {
    const match = part.match(/^\[(\d+)\]$/);
    if (!match) return <span key={`${index}-${part.slice(0, 8)}`}>{part}</span>;
    const number = Number(match[1]);
    return (
      <sup key={`${index}-${number}`}>
        <a onClick={openCitation} href={`#media-citation-${draft.id}-${number}`} aria-label={`Evidence ${number}`}>
          {number}
        </a>
      </sup>
    );
  });
}

function EvidenceLinks({ numbers, draft }: { numbers: number[]; draft: MediaDraft }) {
  return (
    <span className="media-evidence-links" aria-label={`Evidence ${numbers.join(", ")}`}>
      {numbers.map((number) => (
        <a key={number} onClick={openCitation} href={`#media-citation-${draft.id}-${number}`}>{number}</a>
      ))}
    </span>
  );
}

function MediaAssetImage({ draftId, asset }: {
  draftId: string;
  asset: MediaGeneratedAsset;
}) {
  const [source, setSource] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [assetRevision,setAssetRevision]=useState(0);

  useEffect(() => {
    let active = true;
    let objectUrl: string | null = null;
    apiFetch(`/api/media/assets?draftId=${encodeURIComponent(draftId)}&assetId=${encodeURIComponent(asset.id)}`, {
      cache: "force-cache",
    })
      .then(async (response) => {
        if (!response.ok) throw new Error("MEDIA_ASSET_LOAD_FAILED");
        const blob=await response.blob();
        if(active){objectUrl=URL.createObjectURL(blob);setSource(objectUrl);}
      })
      .catch(() => {
        if (active) setError(true);
      });
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [asset.id, draftId,assetRevision]);

  const label = asset.role === "instagram-reel-cover"
    ? "Generated Reel cover"
    : asset.role === "instagram-post-slide" && asset.frameIndex !== undefined && asset.frameCount
      ? `Carousel frame ${asset.frameIndex + 1} of ${asset.frameCount}`
      : "Legacy Instagram image";

  return (
    <figure className={`media-generated-asset ${asset.role === "instagram-reel-cover" ? "vertical" : "portrait"}`}>
      {source ? (
        // The asset is an authenticated in-memory blob URL, which the server-side
        // Next image optimizer cannot fetch without the owner's bearer token.
        // eslint-disable-next-line @next/next/no-img-element
        <img src={source} alt={asset.altText} width={asset.width} height={asset.height} onError={()=>{setSource(null);setError(true);}} />
      ) : (
        <div className="media-asset-placeholder" role={error ? "alert" : "status"}>
          {error ? <><span>Image unavailable</span><button type="button" onClick={()=>{setError(false);setAssetRevision(v=>v+1);}}>Retry image</button></> : "Loading generated image…"}
        </div>
      )}
      <figcaption>
        <span>{label}</span>
        <small>{asset.width}×{asset.height}</small>
        {source&&<a href={source} download={`${draftId}-frame-${(asset.frameIndex??0)+1}.jpg`}>Download image</a>}
      </figcaption>
    </figure>
  );
}

function MediaAssetGallery({ draftId, assets = [] }: {
  draftId: string;
  assets?: MediaGeneratedAsset[];
}) {
  if (!assets.length) return null;
  return (
    <section className="media-generated-assets" aria-label="Generated visual assets">
      {assets.map((asset) => <MediaAssetImage key={asset.id} draftId={draftId} asset={asset} />)}
    </section>
  );
}

function Provenance({ draft }: { draft: MediaDraft }) {
  return (
    <details className="media-provenance">
      <summary>
        <span>Evidence and run</span>
        <small>{draft.citations.length} citations · {draft.evaluatorScore}/100 evaluation</small>
      </summary>
      <div className="media-run-meta">
        <div><span>Agent run</span><strong>{draft.agentRunId}</strong></div>
        <div><span>Core confidence</span><strong>{draft.confidence}/100</strong></div>
        <div><span>Consulted</span><strong>{draft.consultedDomains.join(" · ") || "Core only"}</strong></div>
        <div><span>Template</span><strong>{draft.templateId} v{draft.templateVersion}</strong></div>
        {draft.style && (
          <>
            <div><span>Style skill</span><strong>{draft.style.name} v{draft.style.version}</strong></div>
            <div><span>Inspiration</span><strong>{draft.style.inspirationNames.join(" · ") || "None"}</strong></div>
            {draft.style.referenceCaptions&&<div><span>Source references</span><strong>{draft.style.referenceCaptions.length} images · {draft.style.referenceCaptions.filter(ref=>ref.captionStatus==="verified").length} verified captions</strong>{draft.style.referenceCaptions.map(ref=><a key={ref.id} href={ref.postUrl} target="_blank" rel="noreferrer">{ref.source} · {ref.captionStatus}</a>)}</div>}
          </>
        )}
      </div>
      {draft.limitations.length > 0 && (
        <div className="media-limitations">
          <strong>Limitations</strong>
          <ul>{draft.limitations.map((limitation) => <li key={limitation}>{limitation}</li>)}</ul>
        </div>
      )}
      <ol className="media-citations">
        {draft.citations.map((citation) => (
          <li id={`media-citation-${draft.id}-${citation.number}`} key={citation.number}>
            <span>{citation.number}</span>
            <div>
              {citation.url ? (
                <a href={citation.url} target="_blank" rel="noreferrer">{citation.title}</a>
              ) : <strong>{citation.title}</strong>}
              <p>{citation.observation}</p>
              <small>{citation.kind} · {citation.support} · {citation.recordId}</small>
            </div>
          </li>
        ))}
      </ol>
    </details>
  );
}

function ArticleDraft({ draft }: { draft: MediaArticleDraft }) {
  return (
    <article className="media-article">
      <header>
        <span>Private article draft · not published</span>
        <h2>{draft.title}</h2>
        <p>{draft.dek}</p>
      </header>
      <p className="media-thesis"><CitedText text={draft.thesis} draft={draft} /></p>
      {mediaArticleTemplate.sections.map((section) => (
        <section key={section.id}>
          <h3>{section.heading}</h3>
          {draft.sections[section.id].map((paragraph, paragraphIndex) => (
            <p key={`${section.id}-${paragraphIndex}`}>
              <CitedText text={paragraph} draft={draft} />
            </p>
          ))}
        </section>
      ))}
      <Provenance draft={draft} />
    </article>
  );
}

function ShortVideoBeats({ draft }: { draft: Extract<MediaPlatformDraft, { type: "instagram-reel" | "youtube-short" | "tiktok" }> }) {
  return <ScriptPreview duration={draft.content.durationSeconds} beats={draft.content.beats.map(beat => ({start:beat.startSecond,end:beat.endSecond,label:beat.role,title:beat.onScreenText,text:beat.spokenLine,visual:beat.visualDirection}))}/>;
}

function PlatformDraftView({ draft }: { draft: MediaPlatformDraft }) {
  if (draft.type === "x-post" || draft.type === "threads-post") {
    const characterLimit = draft.type === "x-post" ? 280 : 500;
    return (
      <article className="media-output">
        <header>
          <span>Private {formatLabel(draft.type)} draft · not published</span>
          <h2>{draft.title}</h2>
          <p>{draft.content.format} · {draft.content.posts.length} {draft.content.posts.length === 1 ? "post" : "posts"}</p>
        </header>
        <div className="media-post-stack">
          {draft.content.posts.map((post, index) => (
            <section key={`${draft.id}-post-${index}`}>
              <header>
                <span>{index + 1}/{draft.content.posts.length} · {Array.from(post.text).length}/{characterLimit}</span>
                <EvidenceLinks numbers={post.evidenceNumbers} draft={draft} />
              </header>
              <p>{post.text}</p>
            </section>
          ))}
        </div>
        <section className="media-production-note">
          <h3>Visual direction</h3><p>{draft.content.visualDirection}</p>
        </section>
        <Provenance draft={draft} />
      </article>
    );
  }

  if (draft.type === "instagram-post") {
    return (
      <article className="media-output">
        <header>
          <span>Private Instagram draft · not published</span>
          <h2>{draft.title}</h2>
          <p>{draft.content.format} · {draft.content.slides.length} {draft.content.slides.length === 1 ? "frame" : "frames"}</p>
        </header>
        <section className="media-production-note">
          <h3>Visual concept</h3><p>{draft.content.visualConcept}</p>
        </section>
        <MediaAssetGallery draftId={draft.id} assets={draft.assets} />
        {!draft.assets?.length && <div className="carousel-copy-preview" aria-label="Slide previews">{draft.content.slides.map((slide, index) => <article key={index}><small>{index + 1} / {draft.content.slides.length}</small><h3>{slide.headline}</h3><p>{slide.body}</p></article>)}</div>}
        <details className="media-carousel-source">
          <summary>Slide copy and evidence</summary>
          <div className="media-slide-stack">
            {draft.content.slides.map((slide, index) => (
              <section key={`${draft.id}-slide-${index}`}>
                <header><span>Frame {index + 1}</span><EvidenceLinks numbers={slide.evidenceNumbers} draft={draft} /></header>
                <h3>{slide.headline}</h3><p>{slide.body}</p>
              </section>
            ))}
          </div>
        </details>
        <details className="media-copy-block"><summary>Caption</summary>
          <header><h3>Post description / caption</h3><EvidenceLinks numbers={draft.content.captionEvidenceNumbers} draft={draft} /></header>
          <p>{draft.content.caption}</p>
          <small>{draft.content.caption.length} characters · {draft.content.hashtags.map((tag) => `#${tag}`).join(" ")}</small>
        </details>
        <section className="media-production-note"><h3>Alt text</h3><p>{draft.content.altText}</p></section>
        <Provenance draft={draft} />
      </article>
    );
  }

  if (draft.type === "youtube-video") {
    return (
      <article className="media-output">
        <header>
          <span>Private YouTube video draft · not published</span>
          <h2>{draft.title}</h2>
          <p>{Math.round(draft.content.durationSeconds / 60)} min · thumbnail: {draft.content.thumbnailText}</p>
        </header>
        <ScriptPreview horizontal duration={draft.content.durationSeconds} beats={draft.content.sections.map((section,index) => ({start:index*draft.content.durationSeconds/draft.content.sections.length,end:(index+1)*draft.content.durationSeconds/draft.content.sections.length,label:section.heading,title:section.heading,text:section.narration,visual:section.visualDirection}))}/>
        <p className="muted">Preview pacing divides the script into equal chapters.</p>
        <details className="media-production-note"><summary>Production details</summary><p>Thumbnail: {draft.content.thumbnailText}</p></details>
        <section className="media-copy-block">
          <header><h3>Description</h3><EvidenceLinks numbers={draft.content.descriptionEvidenceNumbers} draft={draft} /></header>
          <p>{draft.content.description}</p>
        </section>
        <details className="media-script-hook"><summary>Opening script</summary>
          <header><h3>Opening</h3><EvidenceLinks numbers={draft.content.hookEvidenceNumbers} draft={draft} /></header>
          <p>{draft.content.hook}</p>
        </details>
        <details className="media-script-sections"><summary>Full script and citations</summary>
          {draft.content.sections.map((section, index) => (
            <section key={`${draft.id}-section-${index}`}>
              <header><h3>{section.heading}</h3><EvidenceLinks numbers={section.evidenceNumbers} draft={draft} /></header>
              <p>{section.narration}</p><small>{section.visualDirection}</small>
            </section>
          ))}
        </details>
        <details className="media-script-hook"><summary>Closing script</summary>
          <header><h3>Close</h3><EvidenceLinks numbers={draft.content.closeEvidenceNumbers} draft={draft} /></header>
          <p>{draft.content.close}</p>
        </details>
        <Provenance draft={draft} />
      </article>
    );
  }

  if (draft.type === "youtube-live" || draft.type === "twitch-live") {
    return (
      <article className="media-output">
        <header>
          <span>Private {formatLabel(draft.type)} plan · not broadcast</span>
          <h2>{draft.title}</h2>
          <p>
            {draft.content.durationMinutes} min live
            {draft.type === "youtube-live"
              ? ` · ${draft.content.streamFormat} · thumbnail: ${draft.content.thumbnailText}`
              : ` · ${draft.content.category} · ${draft.content.tags.join(" · ")}`}
          </p>
        </header>
        <section className="media-copy-block">
          <header><h3>Description</h3><EvidenceLinks numbers={draft.content.descriptionEvidenceNumbers} draft={draft} /></header>
          <p>{draft.content.description}</p>
        </section>
        <details className="media-production-note"><summary>Stream details</summary><p>{draft.type === "youtube-live" ? `${draft.content.streamFormat} · Thumbnail: ${draft.content.thumbnailText}` : `${draft.content.category} · ${draft.content.tags.join(" · ")}`}</p></details>
        <div className="live-rundown"><section><h3>Rundown</h3>{draft.content.segments.map((segment,index) => <details key={index}><summary><span>{segment.startMinute}–{segment.endMinute} min</span><strong>{segment.heading}</strong></summary><p>{segment.hostNotes}</p><p className="muted">{segment.visualDirection}</p>{segment.audiencePrompt && <p>{segment.audiencePrompt}</p>}<EvidenceLinks numbers={segment.evidenceNumbers} draft={draft}/></details>)}</section><aside><h3>Before going live</h3>{draft.content.preShowChecklist.map(item => <label key={item}><input type="checkbox"/>{item}</label>)}</aside></div>
        <section className="media-production-note">
          <h3>{draft.type === "youtube-live" ? "Pinned chat" : "Chat prompt"}</h3>
          <p>{draft.type === "youtube-live" ? draft.content.pinnedChatPrompt : draft.content.chatPrompt}</p>
        </section>
        <section className="media-production-note">
          <h3>Moderator brief</h3><p>{draft.content.moderatorBrief}</p>
        </section>
        <section className="media-production-note media-checklist">
          <h3>Pre-show</h3>
          <ul>{draft.content.preShowChecklist.map((item) => <li key={item}>{item}</li>)}</ul>
        </section>
        {draft.type === "youtube-live" ? (
          <section className="media-production-note"><h3>Replay plan</h3><p>{draft.content.replayPlan}</p></section>
        ) : (
          <>
            <section className="media-production-note"><h3>Scene direction</h3><p>{draft.content.sceneDirection}</p></section>
            <section className="media-production-note"><h3>Clip plan</h3><p>{draft.content.clipPlan}</p></section>
          </>
        )}
        <Provenance draft={draft} />
      </article>
    );
  }

  return (
    <article className="media-output">
      <header>
        <span>Private {formatLabel(draft.type)} draft · not published</span>
        <h2>{draft.title}</h2>
        <p>{draft.content.durationSeconds} sec · 9:16 vertical</p>
      </header>
      <ShortVideoBeats draft={draft} />
      <details className="media-production-note"><summary>Production format</summary><p>{draft.content.durationSeconds} sec · 9:16 vertical</p></details>
      {draft.type === "tiktok" && (
        <section className="media-production-note"><h3>Sound</h3><p>{draft.content.soundDirection}</p></section>
      )}
      {draft.type === "instagram-reel" && (
        <>
          <MediaAssetGallery draftId={draft.id} assets={draft.assets} />
          <section className="media-production-note"><h3>Cover</h3><p>{draft.content.coverText}</p></section>
          <section className="media-production-note"><h3>Audio</h3><p>{draft.content.audioDirection}</p></section>
        </>
      )}
      <section className="media-copy-block">
        <header><h3>Caption</h3><EvidenceLinks numbers={draft.content.captionEvidenceNumbers} draft={draft} /></header>
        <p>{draft.content.caption}</p>
        {(draft.type === "tiktok" || draft.type === "instagram-reel") && (
          <small>{draft.content.hashtags.map((tag) => `#${tag}`).join(" ")}</small>
        )}
      </section>
      <Provenance draft={draft} />
    </article>
  );
}

async function fetchMediaState() {
  const response = await apiFetch("/api/media", { cache: "no-store" });
  const payload = await response.json() as MediaState & { error?: string };
  if (!response.ok) throw new Error(payload.error || "Media could not load.");
  return payload;
}

export default function MediaWorkspace({ onDraftsChange, onOpenSource, focusId, active=true }: {
  active?: boolean;
  focusId?: string | null;
  onDraftsChange?: (drafts: MediaDraft[]) => void;
  onOpenSource?: (sourceId: string) => void;
}) {
  const {document:workspace}=useWorkspaceData();
  const [mediaTab,setMediaTab]=useState("Drafts");
  const [references,setReferences]=useState<MediaReference[]>([]);
  const [referenceIds,setReferenceIds]=useState<string[]|undefined>();
  const [referencesOpen,setReferencesOpen]=useState(false);
  const [referencesError,setReferencesError]=useState("");
  const [variant,setVariant]=useState<"analysis"|"brief"|"single"|"carousel">("analysis");
  const [imageMode,setImageMode]=useState<"typographic"|"illustrated">("illustrated");
  const generationLock=useRef(false);
  const pendingRequest=useRef<{signature:string;requestId:string}|null>(null);
  const [state, setState] = useState<MediaState | null>(null);
  const [mediaType, setMediaType] = useState<MediaOutputType>("article");
  const [selectedDraftId, setSelectedDraftId] = useState<string | null>(focusId ?? null);
  useEffect(() => { if(active)window.scrollTo({top:0}); }, [selectedDraftId,active]);
  const [lastFocus,setLastFocus]=useState(focusId);
  if(focusId!==lastFocus){setLastFocus(focusId);if(focusId)setSelectedDraftId(focusId);}
  const [draftFilter, setDraftFilter] = useState("all");
  const [stylesOpen, setStylesOpen] = useState(false);
  const [copyStatus, setCopyStatus] = useState<string | null>(null);
  const [objective, setObjective] = useState("");
  const [busy, setBusy] = useState<Busy>("load");
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setBusy("load");
    setError(null);
    try {
      const next = await fetchMediaState();
      setState(next);
      onDraftsChange?.(next.drafts);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Media could not load.");
    } finally {
      setBusy(null);
    }
  }, [onDraftsChange]);

  useEffect(() => {
    let active = true;
    fetchMediaState()
      .then((payload) => {
        if (active) {
          setState(payload);
          onDraftsChange?.(payload.drafts);
        }
      })
      .catch((loadError: unknown) => {
        if (active) setError(loadError instanceof Error ? loadError.message : "Media could not load.");
      })
      .finally(() => {
        if (active) setBusy(null);
      });
    return () => { active = false; };
  }, [onDraftsChange]);

  useEffect(()=>{let live=true;apiFetch("/api/media/references").then(async response=>{if(!response.ok)throw new Error("Reference library unavailable. Open Inspiration to retry.");return await response.json() as {references:MediaReference[]};}).then(data=>{if(live){setReferences(data.references);setReferencesError("");}}).catch(()=>{if(live)setReferencesError("Reference library unavailable. Open Inspiration to retry.");});return()=>{live=false;};},[]);
  const chosenReferences=(referenceIds??chooseMediaReferences(references).map(r=>r.id)).map(id=>references.find(r=>r.id===id&&r.approved)).filter((r):r is MediaReference=>Boolean(r));
  const routing=resolveMediaModelRouting({mediaType,writer:{provider:workspace.providers.research,model:workspace.providers.model},referenceCount:chosenReferences.length,imageMode,imageModel:workspace.providers.imageModel},state?.modelDefaults);

  async function generate(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if(generationLock.current || busy || !state?.configured)return;
    generationLock.current=true;
    setBusy("generate");
    setError(null);
    try {
      const body = mediaType === "article"
        ? { action: "generate_article", objective: objective.trim() }
        : { action: "generate_platform", mediaType, objective: objective.trim() };
      const selectedIds=(referenceIds??chooseMediaReferences(references).map(r=>r.id)).filter(id=>references.some(reference=>reference.id===id&&reference.approved));
      const options={referenceIds:mediaType==="instagram-post"?selectedIds:undefined,variant:mediaVariants(mediaType).includes(variant)?variant:undefined,imageMode:mediaType==="instagram-post"?imageMode:undefined,provider:workspace.providers.research,model:workspace.providers.model,imageModel:routing.image?workspace.providers.imageModel:undefined};
      const signature=JSON.stringify({...body,...options});
      if(pendingRequest.current?.signature!==signature)pendingRequest.current={signature,requestId:crypto.randomUUID()};
      const response = await apiFetch("/api/media", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({...body,...options,requestId:pendingRequest.current.requestId}),
        signal:AbortSignal.timeout(900_000),
      });
      const payload = await response.json() as MediaState & { error?: string };
      if (!response.ok) throw new Error(payload.error || "The draft could not be generated.");
      pendingRequest.current=null;
      setState(payload);
      onDraftsChange?.(payload.drafts);
      setObjective("");
      setSelectedDraftId(payload.drafts[0]?.id ?? null);
    } catch (generationError) {
      setError(generationError instanceof Error ? generationError.message : "The draft could not be generated.");
    } finally {
      generationLock.current=false;
      setBusy(null);
    }
  }

  const formats = state?.formats ?? mediaOutputFormats;
  const selectedFormat = formats.find((format) => format.id === mediaType) ?? mediaOutputFormats[0];
  const drafts = state?.drafts ?? [];
  const filteredDrafts = drafts.filter(draft => draftFilter === "all" || draftFilter === "article" && draft.type === "article" || draftFilter === "posts" && ["x-post", "threads-post", "instagram-post"].includes(draft.type) || draftFilter === "video" && ["instagram-reel", "youtube-short", "youtube-video", "tiktok"].includes(draft.type) || draftFilter === "live" && draft.type.endsWith("live"));
  const selectedDraft = drafts.find(draft => draft.id === selectedDraftId);
  async function copyDraft(draft: MediaDraft) {
    try { await navigator.clipboard.writeText(mediaDraftText(draft)); setCopyStatus("Copied"); }
    catch { setCopyStatus("Clipboard unavailable. Use Download draft."); }
  }
  function downloadDraft(draft: MediaDraft) {
    const url = URL.createObjectURL(new Blob([mediaDraftText(draft)], { type: "text/plain;charset=utf-8" }));
    const link = document.createElement("a"); link.href = url; link.download = `${draft.title.replace(/[^a-z0-9]+/gi, "-").slice(0, 80)}.txt`; link.click(); setTimeout(()=>URL.revokeObjectURL(url),1000);
  }
  if (selectedDraft) return <section className={`workspace media-workspace media-detail media-detail-${selectedDraft.type}`} id={active ? "workspace" : undefined} hidden={!active} aria-labelledby="media-title">
    <div className="media-detail-toolbar"><button type="button" className="back-link" onClick={() => { setSelectedDraftId(null); setCopyStatus(null); }}>← Drafts</button><div><button type="button" onClick={() => void copyDraft(selectedDraft)}>{["instagram-reel", "youtube-short", "tiktok", "youtube-video", "youtube-live", "twitch-live"].includes(selectedDraft.type) ? "Copy script" : "Copy draft"}</button><details><summary>More</summary><button type="button" onClick={() => downloadDraft(selectedDraft)}>Download draft</button></details></div></div>
    {copyStatus && <p role="status" className="muted">{copyStatus}</p>}
    <header className="page-heading"><div><h1 id="media-title">{selectedDraft.title}</h1><p className="lede">Private draft · {formatLabel(selectedDraft.type)} · {draftMetric(selectedDraft)}</p></div></header>
    {selectedDraft.style?.routing && <MediaRouting routing={selectedDraft.style.routing} mediaType={selectedDraft.type}/>}
    <div className="media-reading-grid"><div>{selectedDraft.type === "article" ? <ArticleDraft draft={selectedDraft}/> : <PlatformDraftView draft={selectedDraft}/>}</div><aside className="media-evidence-aside"><h3>Evidence</h3>{selectedDraft.citations.map(citation => <details key={citation.number}><summary>{citation.title}</summary><p>{citation.observation}</p>{citation.url && <a href={citation.url} target="_blank" rel="noreferrer">Open source ↗</a>}</details>)}<details><summary>Evidence gaps</summary>{selectedDraft.limitations.length ? selectedDraft.limitations.map(item => <p key={item}>{item}</p>) : <p>No additional gaps recorded.</p>}</details></aside></div>
    <footer className="media-rewrite"><button type="button" disabled={!state?.configured} onClick={() => { const selection = window.getSelection()?.toString().trim(); setMediaType(selectedDraft.type); setVariant(selectedDraft.type==="instagram-post"?(selectedDraft.content.format==="single-image"?"single":"carousel"):selectedDraft.style?.variant==="brief"?"brief":"analysis"); setObjective(`Rewrite ${selection ? `this selected passage: ${selection}` : `the draft titled ${selectedDraft.title}`}. Preserve the evidence and source citations. ` .slice(0, 1900)); setSelectedDraftId(null); }}>Rewrite selection</button><details><summary>Style · {selectedDraft.style?.name ?? "Asymmetric"}</summary>{state?.styleSystem && <MediaStyleEditor key={`${selectedDraft.type}-${state.styleSystem.activeSkillByMediaType[selectedDraft.type]}`} system={state.styleSystem} mediaType={selectedDraft.type} disabled={false} onSystemChange={styleSystem => setState(current => current ? { ...current, styleSystem } : current)} onError={setError} onOpenSource={onOpenSource}/>}</details></footer>{error && <p role="alert">{error}</p>}
  </section>;

  return (
    <section className="workspace media-workspace" id={active ? "workspace" : undefined} hidden={!active} aria-labelledby="media-title">
      <header className="page-heading media-heading">
        <div><h1 id="media-title">{mediaTab==="Drafts"&&mediaType==="instagram-post"?"New Instagram draft":"Media"}</h1><p className="lede">Turn research into a draft.</p></div>
        <button type="button" aria-expanded={stylesOpen} onClick={() => setStylesOpen(value => !value)}>Styles →</button>
      </header>

      <nav className="quiet-tabs" aria-label="Media sections">{["Drafts","Inspiration"].map(tab=><button type="button" key={tab} aria-pressed={mediaTab===tab} onClick={()=>setMediaTab(tab)}>{tab}</button>)}</nav>
      {mediaTab==="Inspiration" ? <MediaInspiration references={references} onChange={setReferences} disabled={busy==="generate"}/> : <>
      <div className="media-creation-settings">          <label className="media-format-control"><span>Format:</span><select aria-label="Media format" value={mediaType} disabled={busy === "generate"} onChange={event => {setMediaType(event.target.value as MediaOutputType);setVariant(event.target.value==="article"?"analysis":"carousel");}}>{formats.map(format => <option key={format.id} value={format.id}>{format.label}</option>)}</select></label>
          <div className="media-format-control"><span>Style:</span><button type="button" aria-expanded={stylesOpen} onClick={() => setStylesOpen(value => !value)}>{state?.styleSystem.skills.find(skill => skill.id === state.styleSystem.activeSkillByMediaType[mediaType])?.name ?? "Asymmetric"}⌄</button></div>
</div>
      <div className="media-creation-layout" data-format={mediaType}><div><form className="media-generator media-objective-generator" onSubmit={generate}>
        <label htmlFor="media-objective">{selectedFormat.label} objective</label>
        <textarea
          id="media-objective"
          value={objective}
          maxLength={2_000}
          onChange={(event) => setObjective(event.target.value)}
          placeholder="What would you like to create?"
        />
        <footer>
          <button type="submit" disabled={Boolean(busy) || !state?.configured}>
            {busy === "generate" ? "Generating…" : "Generate draft"}
          </button>
        </footer>
      </form>
      <div className="media-generation-options">{mediaVariants(mediaType).length>0&&<label>Variant<select aria-label="Generation variant" disabled={busy==="generate"} value={variant} onChange={e=>setVariant(e.target.value as typeof variant)}>{mediaType==="article"?<><option value="analysis">Full analysis</option><option value="brief">Brief article</option></>:<><option value="carousel">Carousel</option><option value="single">Single image</option></>}</select></label>}{mediaType==="instagram-post"&&<><label>Visuals<select aria-label="Instagram visuals" disabled={busy==="generate"} value={imageMode} onChange={e=>setImageMode(e.target.value as typeof imageMode)}><option value="illustrated">Illustrated</option><option value="typographic">Typographic</option></select></label><button type="button" aria-expanded={referencesOpen} onClick={()=>setReferencesOpen(v=>!v)}>Choose references →</button></>}</div><MediaRouting routing={routing} mediaType={mediaType}/>
      </div>{mediaType==="instagram-post"&&<aside className="generation-reference-preview"><h3>What the model will see</h3><p className="muted">{chosenReferences.length} images · {chosenReferences.filter(r=>r.captionStatus==="verified").length} original captions</p>{referencesError&&!references.length&&<p role="alert">{referencesError}</p>}<div className="selected-reference-grid">{chosenReferences.slice(0,4).map(reference=><article key={reference.id}><ReferenceImage reference={reference}/><small>{reference.attribution}</small><p>{reference.captionStatus==="verified"?reference.caption:"Caption to review"}</p></article>)}</div>{!chosenReferences.length&&<p className="muted">No visual references selected.</p>}{chosenReferences.length>4&&<p className="muted">+{chosenReferences.length-4} more selected references</p>}<button type="button" onClick={()=>setReferencesOpen(value=>!value)}>Review selected references →</button><p className="muted">References guide style. Research evidence supplies the facts.</p></aside>}</div>
      {mediaType==="instagram-post"&&referencesOpen&&<MediaInspiration picker references={references} onChange={setReferences} selected={referenceIds??chooseMediaReferences(references).map(r=>r.id)} onSelect={setReferenceIds} disabled={busy==="generate"}/>}
      {stylesOpen && state?.styleSystem && <MediaStyleEditor initiallyOpen key={`${mediaType}:${state.styleSystem.activeSkillByMediaType[mediaType]}`} system={state.styleSystem} mediaType={mediaType} disabled={busy === "generate"} onSystemChange={styleSystem => setState(current => current ? { ...current, styleSystem } : current)} onError={setError} onOpenSource={onOpenSource}/>}

      {error && (
        <div className="media-error" role="alert">
          <span>Media error</span><p>{error}</p>
          <button type="button" onClick={() => void load()} disabled={Boolean(busy)}>Retry</button>
        </div>
      )}

      {!state?.configured && busy !== "load" && <p className="muted">Draft generation is unavailable in this environment. Saved drafts remain available.</p>}
      <h2 className="draft-library-heading">Recent drafts</h2><nav className="quiet-tabs" aria-label="Draft formats">{["all", "article", "posts", "video", "live"].map(value => <button type="button" key={value} aria-pressed={draftFilter === value} onClick={() => setDraftFilter(value)}>{value === "all" ? "All drafts" : value[0].toUpperCase() + value.slice(1)}</button>)}</nav>
      <section className="media-register" aria-live="polite" aria-busy={busy === "generate"}>
        <header><span>Drafts</span><strong>{drafts.length}</strong></header>
        {drafts.length > 0 && !filteredDrafts.length && <p className="empty-state">No drafts in this format yet.</p>}
        {!drafts.length && busy !== "load" ? (
          <div className="media-empty">
            <strong>No drafts yet.</strong>
            <span>Choose a format and generate from the current engine state.</span>
          </div>
        ) : filteredDrafts.map(draft => (
          <details className="media-draft" id={`media-draft-${draft.id}`} key={draft.id}>
            <summary onClick={event => { event.preventDefault(); setSelectedDraftId(draft.id); }}>
              <div>
                <strong>{draft.title}</strong>

              </div>
              <span>{formatLabel(draft.type)}</span><small>{timeLabel(draft.generatedAt)}</small>
            </summary>
            {draft.type === "article" ? <ArticleDraft draft={draft} /> : <PlatformDraftView draft={draft} />}
          </details>
        ))}
      </section>

      </>}
      <p className="boundary">Private drafts only. Media has no publishing, messaging, portfolio-write, or order authority.</p>
    </section>
  );
}
